import { PayloadAction } from '@reduxjs/toolkit';

import {
    ImmunizationRefusalReasonDto,
    ImmunizationRegistryClient,
    ImmunizationRegistryCodeTablesDto,
    ImmunizationRouteCodeDto,
    LookupClient,
    PatientImmunizationDto,
    PatientImmunizationsClient,
    RegistryStaffDto,
    UpsertPatientImmunizationRequestDto,
} from '@medone/medonehp-api-client';

import { handleError, toastError } from '../../../../shared/common/HandleErrors';
import { shouldSkipByPatientId } from '../../../../shared/common/helpers';
import { Axios } from '../../../../shared/common/http';
import { AppDispatch, AppThunk, RootState } from '../../../../shared/store';
import { CensusState } from './models';
import { censusSlice } from './slice';

export const reducers = {
    setPatientImmunizationsLoading: (state: CensusState, action: PayloadAction<boolean>) => {
        state.patientImmunizationsLoading = action.payload;
    },

    setPatientImmunizations: (state: CensusState, action: PayloadAction<PatientImmunizationDto[]>) => {
        state.patientImmunizations = action.payload;
    },

    setPatientImmunizationProcessing: (state: CensusState, action: PayloadAction<boolean>) => {
        state.patientImmunizationProcessing = action.payload;
    },

    setPreventativePatientImmunizationsLoading: (state: CensusState, action: PayloadAction<boolean>) => {
        state.preventativePatientImmunizationsLoading = action.payload;
    },

    setPreventativePatientImmunizations: (state: CensusState, action: PayloadAction<PatientImmunizationDto[]>) => {
        state.preventativePatientImmunizations = action.payload;
    },

    setImmunizationRefusalReasons: (state: CensusState, action: PayloadAction<ImmunizationRefusalReasonDto[]>) => {
        state.immunizationRefusalReasons = action.payload;
        state.immunizationRefusalReasonsLoaded = true;
    },

    setImmunizationRouteCodes: (state: CensusState, action: PayloadAction<ImmunizationRouteCodeDto[]>) => {
        state.immunizationRouteCodes = action.payload;
        state.immunizationRouteCodesLoaded = true;
    },

    setImmunizationRegistryCodes: (state: CensusState, action: PayloadAction<ImmunizationRegistryCodeTablesDto>) => {
        state.immunizationRegistryCodes = action.payload;
    },

    setImmunizationRegistryStaff: (state: CensusState, action: PayloadAction<RegistryStaffDto[]>) => {
        state.immunizationRegistryStaff = action.payload;
        state.immunizationRegistryStaffLoaded = true;
    },
};

/**
 * Loads a patient's structured immunizations. `preventativeOnly` filters server-side to the
 * preventative-group CVX codes and stores into a separate list, so the Preventive tab and the
 * Medical Summary panel (which both reuse this state) don't clobber each other.
 */
export function fetchPatientImmunizations(patientId: number, options?: { preventativeOnly?: boolean; bypassActivePatientGuard?: boolean }): AppThunk {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const preventativeOnly = options?.preventativeOnly === true;
        const setLoading = preventativeOnly
            ? censusSlice.actions.setPreventativePatientImmunizationsLoading
            : censusSlice.actions.setPatientImmunizationsLoading;
        const setList = preventativeOnly
            ? censusSlice.actions.setPreventativePatientImmunizations
            : censusSlice.actions.setPatientImmunizations;

        dispatch(setLoading(true));

        const client = new PatientImmunizationsClient(null, Axios);

        try {
            const response = await client.getByPatient(patientId, preventativeOnly || undefined);

            // Guard against race condition: discard if user navigated to a different patient. The census chart
            // tracks the active patient via currentCensus; census-less routes (e.g. the Clinical Coordinator AWV
            // form, which loads one patient) opt out via bypassActivePatientGuard.
            if (!options?.bypassActivePatientGuard && shouldSkipByPatientId(getState().census, patientId)) {
                return;
            }

            if (response.result.succeeded) {
                dispatch(setList(response.result.entity ?? []));
            } else {
                handleError(response, () => dispatch(censusSlice.actions.setError(null)));
            }
        } catch (error) {
            handleError(error, () => dispatch(censusSlice.actions.setError(error.toString())));
        }

        dispatch(setLoading(false));
    };
}

/** Reference catalog of SNOMED refusal reasons. Session-cached: fetched once, reused for the rest of the session. */
export function fetchImmunizationRefusalReasons(): AppThunk {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        if (getState().census.immunizationRefusalReasonsLoaded) {
            return;
        }

        const client = new LookupClient(null, Axios);

        try {
            const response = await client.immunizationRefusalReasons();

            if (response.result.succeeded) {
                dispatch(censusSlice.actions.setImmunizationRefusalReasons(response.result.entity ?? []));
            } else {
                handleError(response, () => dispatch(censusSlice.actions.setError(null)));
            }
        } catch (error) {
            handleError(error, () => dispatch(censusSlice.actions.setError(error.toString())));
        }
    };
}

/** Reference catalog of SNOMED immunization route codes (small fixed list). Session-cached: fetched once. */
export function fetchImmunizationRouteCodes(): AppThunk {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        if (getState().census.immunizationRouteCodesLoaded) {
            return;
        }

        const client = new LookupClient(null, Axios);

        try {
            const response = await client.immunizationRouteCodes();

            if (response.result.succeeded) {
                dispatch(censusSlice.actions.setImmunizationRouteCodes(response.result.entity ?? []));
            } else {
                handleError(response, () => dispatch(censusSlice.actions.setError(null)));
            }
        } catch (error) {
            handleError(error, () => dispatch(censusSlice.actions.setError(error.toString())));
        }
    };
}

/** SCRUM-1570: the HL7 v2 code tables behind the registry fields. Static, so session-cached like the route codes. */
export function fetchImmunizationRegistryCodes(): AppThunk {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        if (getState().census.immunizationRegistryCodes != null) {
            return;
        }

        const client = new LookupClient(null, Axios);

        try {
            const response = await client.immunizationRegistryCodes();

            if (response.result.succeeded && response.result.entity != null) {
                dispatch(censusSlice.actions.setImmunizationRegistryCodes(response.result.entity));
            } else {
                handleError(response, () => dispatch(censusSlice.actions.setError(null)));
            }
        } catch (error) {
            handleError(error, () => dispatch(censusSlice.actions.setError(error.toString())));
        }
    };
}

/** SCRUM-1570: active users for the administering and ordering provider pickers. Session-cached. */
export function fetchImmunizationRegistryStaff(): AppThunk {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        if (getState().census.immunizationRegistryStaffLoaded) {
            return;
        }

        const client = new ImmunizationRegistryClient(null, Axios);

        try {
            const response = await client.getStaff();

            if (response.result.succeeded) {
                dispatch(censusSlice.actions.setImmunizationRegistryStaff(response.result.entity ?? []));
            } else {
                handleError(response, () => dispatch(censusSlice.actions.setError(null)));
            }
        } catch (error) {
            handleError(error, () => dispatch(censusSlice.actions.setError(error.toString())));
        }
    };
}

export function savePatientImmunization(
    mode: 'create' | 'edit',
    patientId: number,
    immunizationId: number | undefined,
    dto: UpsertPatientImmunizationRequestDto,
    options?: { preventativeOnly?: boolean; bypassActivePatientGuard?: boolean }
): AppThunk<Promise<boolean>> {
    return async (dispatch: AppDispatch) => {
        dispatch(censusSlice.actions.setPatientImmunizationProcessing(true));

        const client = new PatientImmunizationsClient(null, Axios);

        try {
            const response = mode === 'create' ? await client.post(patientId, dto) : await client.put(patientId, immunizationId, dto);

            if (response.result.succeeded) {
                await dispatch(fetchPatientImmunizations(patientId, options));
                return true;
            }

            // Optimistic concurrency: a stale RowVersion surfaces in the envelope (succeeded=false),
            // not as a 400/409. Surface the server message and refetch so the user sees the live record.
            toastError(response.result.message || 'The record was modified by another user. Refresh and try again.');
            await dispatch(fetchPatientImmunizations(patientId, options));
        } catch (error) {
            handleError(error, () => dispatch(censusSlice.actions.setError(error.toString())));
        } finally {
            dispatch(censusSlice.actions.setPatientImmunizationProcessing(false));
        }

        return false;
    };
}

/** `options` as on save: the list a surface shows (the preventative one, or a census-less route's) is the one re-fetched. */
export function deletePatientImmunization(
    patientId: number,
    immunizationId: number,
    options?: { preventativeOnly?: boolean; bypassActivePatientGuard?: boolean }
): AppThunk<Promise<boolean>> {
    return async (dispatch: AppDispatch) => {
        dispatch(censusSlice.actions.setPatientImmunizationProcessing(true));

        const client = new PatientImmunizationsClient(null, Axios);

        try {
            const response = await client.delete(patientId, immunizationId);

            if (response.result.succeeded) {
                await dispatch(fetchPatientImmunizations(patientId, options));
                return true;
            }

            handleError(response, () => dispatch(censusSlice.actions.setError(null)));
        } catch (error) {
            handleError(error, () => dispatch(censusSlice.actions.setError(error.toString())));
        } finally {
            dispatch(censusSlice.actions.setPatientImmunizationProcessing(false));
        }

        return false;
    };
}

export const selectPatientImmunizations = (state: RootState) => state.census.patientImmunizations;
export const selectPatientImmunizationsLoading = (state: RootState) => state.census.patientImmunizationsLoading;
export const selectPreventativePatientImmunizations = (state: RootState) => state.census.preventativePatientImmunizations;
export const selectPreventativePatientImmunizationsLoading = (state: RootState) => state.census.preventativePatientImmunizationsLoading;
export const selectPatientImmunizationProcessing = (state: RootState) => state.census.patientImmunizationProcessing;
export const selectImmunizationRefusalReasons = (state: RootState) => state.census.immunizationRefusalReasons;
export const selectImmunizationRouteCodes = (state: RootState) => state.census.immunizationRouteCodes;
export const selectImmunizationRegistryCodes = (state: RootState) => state.census.immunizationRegistryCodes;
export const selectImmunizationRegistryStaff = (state: RootState) => state.census.immunizationRegistryStaff;
